import { signalStore, withMethods, withState } from '@ngrx/signals';
import { updateState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { inject } from '@angular/core';
import { TenantColorConfig } from '@clientwire/api';
import { AppTypeService, WireApiService } from '../services';
import { ErrorStore } from './error.store';
import { FaviconStore } from './favicon.store';

declare global {
  interface Window {
    spaConfig?: {
      tenant_domain?: string;
    };
  }
}

type TenantState = {
  id: string;
  frontendUrl: string;
  name: string;
  enabled: boolean;
  inited: boolean;
  error: string | null;
  preferredLanguage: string | null;
};

const initialState: TenantState = {
  id: '',
  frontendUrl: '',
  name: '',
  enabled: true,
  inited: false,
  error: null,
  preferredLanguage: null,
};

/**
 * Applies the tenant's theme as CSS custom properties on the document root.
 * Shared by every tenant lookup, so the theme is applied no matter whether the
 * tenant was resolved by domain (main/client app) or by id (embed lib).
 */
const applyColorConfig = (colorConfig: TenantColorConfig, isClientApp: boolean) => {
  const cssVariables = isClientApp
    ? {
        '--button-primary-bg': colorConfig.clientButtonColor,
        '--button-primary-bg-hover': colorConfig.clientButtonHoverColor,
        '--button-label-color': colorConfig.clientButtonLabelColor,
        '--sender-chat-bubble-color': colorConfig.clientSenderChatBubbleColor,
        '--sender-chat-bubble-text-color': colorConfig.clientSenderChatBubbleTextColor,
        '--participant-chat-bubble-color': colorConfig.clientParticipantChatBubbleColor,
        '--participant-chat-bubble-text-color': colorConfig.clientParticipantChatBubbleTextColor,
      }
    : {
        '--background-color': colorConfig.headerColor,
        '--button-primary-bg': colorConfig.buttonColor,
        '--button-primary-bg-hover': colorConfig.buttonHoverColor,
        '--button-label-color': colorConfig.buttonLabelColor,
        '--sender-chat-bubble-color': colorConfig.senderChatBubbleColor,
        '--sender-chat-bubble-text-color': colorConfig.senderChatBubbleTextColor,
        '--participant-chat-bubble-color': colorConfig.participantChatBubbleColor,
        '--participant-chat-bubble-text-color': colorConfig.participantChatBubbleTextColor,
        // Selected item background (chat list, settings nav). Left unset when the
        // tenant has no value, so the SCSS fallback to --highlight-blue applies.
        '--active-item-bg': colorConfig.activeConversationColor,
        //
        '--client-button-primary-bg': colorConfig.clientButtonColor,
        '--client-button-primary-bg-hover': colorConfig.clientButtonHoverColor,
        '--client-button-label-color': colorConfig.clientButtonLabelColor,
        '--client-sender-chat-bubble-color': colorConfig.clientSenderChatBubbleColor,
        '--client-sender-chat-bubble-text-color': colorConfig.clientSenderChatBubbleTextColor,
        '--client-participant-chat-bubble-color': colorConfig.clientParticipantChatBubbleColor,
        '--client-participant-chat-bubble-text-color':
          colorConfig.clientParticipantChatBubbleTextColor,
      };

  Object.entries(cssVariables).forEach(([property, value]) => {
    if (value) {
      document.documentElement.style.setProperty(property, value);
    }
  });
};

export type TenantStore = InstanceType<typeof TenantStore>;

export const TenantStore = signalStore(
  { providedIn: 'root' },
  withDevtools('tenant-store'),
  withState(initialState),
  withMethods((store) => {
    const wireApiService = inject(WireApiService);
    const appType = inject(AppTypeService);
    const errorStore = inject(ErrorStore);
    const faviconStore = inject(FaviconStore);
    return {
      async getById(id: string) {
        try {
          const response = await wireApiService.tenantConfigApi.getTenantConfig({ tenantId: id });

          if (response.colorConfig) {
            applyColorConfig(response.colorConfig, appType.type() === 'client');
          }
          updateState(store, 'Set tenant byId', {
            id: response.id,
            frontendUrl: response.frontendUrl,
            name: response.name,
            enabled: response.enabled,
            inited: true,
            preferredLanguage: response.preferredLanguage,
          });

          await faviconStore.updateFavicon(response.id);
        } catch (error) {
          console.error('Failed to fetch tenant config by ID:', error);
          updateState(store, 'Mark tenant as initialized with error', {
            inited: true,
            error: 'error.tenantConfig.loadFailed',
          });
          try {
            await errorStore.addErrorResponse(error, 'global');
          } catch {
            errorStore.addError({
              code: 'error.tenantConfig.unknown',
              type: 'global',
            });
          }
        }
      },
      async getConfigByDomain(domain: string) {
        try {
          const response = await wireApiService.tenantConfigApi.getTenantConfig({
            domain: domain,
          });

          if (response.colorConfig) {
            applyColorConfig(response.colorConfig, appType.type() === 'client');
          }
          updateState(store, 'Set tenant bySubdomain', {
            id: response.id,
            frontendUrl: response.frontendUrl,
            name: response.name,
            enabled: response.enabled,
            inited: true,
            preferredLanguage: response.preferredLanguage,
          });

          await faviconStore.updateFavicon(response.id);
        } catch (error) {
          updateState(store, 'Mark tenant as initialized with error', {
            inited: true,
            error: 'error.tenantConfig.loadFailed',
          });
          try {
            await errorStore.addErrorResponse(error, 'global');
          } catch {
            errorStore.addError({
              code: 'error.unknown',
              type: 'global',
            });
          }
        }
      },

      removeTenant() {
        updateState(store, 'Clear tenant', {
          id: '',
          frontendUrl: '',
          name: '',
          enabled: true,
          preferredLanguage: null,
        });
      },

      setFrontendUrl(frontendUrl: string) {
        updateState(store, 'Set frontend URL', { frontendUrl });
      },

      async init() {
        const appType = inject(AppTypeService);
        // An explicitly configured tenant wins for every app type. The embed lib always has one;
        // the client app has one when it runs inside the embed iframe, where location.host would
        // name the embed host instead of the tenant.
        const domain = appType.domain();
        if (domain) {
          this.setFrontendUrl(domain);
          await this.getConfigByDomain(store.frontendUrl());
          return;
        }
        const tenantId = appType.tenantId();
        if (tenantId) {
          await this.getById(tenantId);
          return;
        }
        if (appType.type() === 'embed') {
          updateState(store, 'No domain or tenantId found', { inited: true });
          throw new Error('No domain or tenantId found');
        }
        const spaDomain = window.spaConfig?.tenant_domain;
        const fullDomain = spaDomain ? spaDomain : window.location.host.split(':')[0];
        if (fullDomain) {
          await this.getConfigByDomain(fullDomain);
        } else {
          updateState(store, 'No subdomain found', { inited: true });
          throw new Error('No domain found');
        }
      },
    };
  })
);
